import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { userApi } from 'shared/services/userApi';
import { notificationApi } from 'shared/services/notificationApi';

interface EligibleUser {
  id: string;
  name: string;
  email: string;
  status: string;
}

const STATUS_LABEL: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
  approved: { label: 'Verificado', color: 'success' },
  pending: { label: 'Pendiente', color: 'warning' },
  in_review: { label: 'En revisión', color: 'warning' },
  rejected: { label: 'Rechazado', color: 'error' },
  expired: { label: 'Vencido', color: 'error' },
  not_submitted: { label: 'No iniciado', color: 'default' },
};

interface NotifyVerificationDialogProps {
  open: boolean;
  companyId: string;
  verificationId: string | null;
  verificationName: string;
  onClose: () => void;
}

/**
 * Lets an admin send a reminder email to whoever this specific
 * verification rule applies to. Eligibility is read straight off each
 * user's `verificationSummary` (already computed server-side via
 * `verificationRuleAppliesToUser` — see verificationService.ts) by
 * matching on this rule's id, rather than re-deriving the scope rules
 * (appliesTo / scopedProviderCompanies / requireFromCompanyAdmin) here.
 */
export const NotifyVerificationDialog: React.FC<NotifyVerificationDialogProps> = ({
  open,
  companyId,
  verificationId,
  verificationName,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<EligibleUser[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ sent: number; failed: number } | null>(null);

  useEffect(() => {
    if (!open || !verificationId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setResult(null);
    (async () => {
      try {
        const res = await userApi.getAll({ companyId, limit: 100 } as any);
        const roster: any[] = (res?.data || []) as any[];
        const eligible: EligibleUser[] = [];
        roster.forEach((u: any) => {
          const detail = (u.verificationSummary?.details || []).find(
            (d: any) => String(d.companyId) === String(companyId),
          );
          const rule = (detail?.verifications || []).find(
            (v: any) => String(v.id) === String(verificationId),
          );
          if (!rule) return; // rule doesn't apply to this user
          eligible.push({
            id: String(u._id || u.id),
            name: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || '—',
            email: u.email || '—',
            status: rule.status || 'not_submitted',
          });
        });
        if (cancelled) return;
        setUsers(eligible);
        // Default selection: everyone who hasn't actually completed it —
        // "notify all" in the common case means "everyone still missing
        // it", but every eligible person stays visible/toggleable in case
        // someone wants to re-notify an already-verified person too.
        setSelected(new Set(eligible.filter((u) => u.status !== 'approved').map((u) => u.id)));
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'No se pudo cargar la lista de personas');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, verificationId, companyId]);

  const allSelected = users.length > 0 && selected.size === users.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(users.map((u) => u.id)));
  };
  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSend = async () => {
    if (selected.size === 0) return;
    setSending(true);
    setResult(null);
    try {
      const targets = Array.from(selected);
      const outcomes = await Promise.all(
        targets.map((userId) =>
          notificationApi.sendManualNotification({
            targetUserId: userId,
            companyId,
            channel: 'email',
            onlyRequired: true,
          }).then((r) => r.success).catch(() => false),
        ),
      );
      const sent = outcomes.filter(Boolean).length;
      setResult({ sent, failed: outcomes.length - sent });
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (sending) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Notificar: {verificationName}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : users.length === 0 ? (
          <Alert severity="info">Nadie cumple con el alcance de esta verificación.</Alert>
        ) : (
          <>
            {result && (
              <Alert
                severity={result.failed === 0 ? 'success' : 'warning'}
                sx={{ mb: 1.5 }}
              >
                {result.sent} notificación{result.sent === 1 ? '' : 'es'} enviada{result.sent === 1 ? '' : 's'}
                {result.failed > 0 ? `, ${result.failed} fallida${result.failed === 1 ? '' : 's'}` : ''}.
              </Alert>
            )}
            <ListItemButton onClick={toggleAll} dense sx={{ borderRadius: 1, mb: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Checkbox
                  edge="start"
                  checked={allSelected}
                  indeterminate={someSelected}
                  tabIndex={-1}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText
                primary={<Typography fontWeight={600}>Notificar a todos</Typography>}
                secondary={`${users.length} persona${users.length === 1 ? '' : 's'} cumple${users.length === 1 ? '' : 'n'} con esta verificación`}
              />
            </ListItemButton>
            <Divider sx={{ mb: 0.5 }} />
            <List dense sx={{ maxHeight: 320, overflowY: 'auto' }}>
              {users.map((u) => {
                const statusMeta = STATUS_LABEL[u.status] || STATUS_LABEL.not_submitted;
                return (
                  <ListItemButton key={u.id} onClick={() => toggleOne(u.id)} dense>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Checkbox
                        edge="start"
                        checked={selected.has(u.id)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={u.name}
                      secondary={u.email}
                      sx={{ mr: 1 }}
                    />
                    <Chip
                      label={statusMeta.label}
                      size="small"
                      color={statusMeta.color === 'default' ? undefined : statusMeta.color}
                      variant="outlined"
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={sending}>Cerrar</Button>
        <Button
          onClick={handleSend}
          variant="contained"
          disabled={sending || loading || selected.size === 0}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {sending ? 'Enviando...' : `Notificar (${selected.size})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotifyVerificationDialog;
